import { useEffect, useState } from "react";
import { usePageMeta } from "@/lib/usePageMeta";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { InsulatorCard } from "@/components/InsulatorCard";
import { AdSlot } from "@/components/AdSlot";
import { HoneyDropMorph } from "@/components/HoneyDropMorph";
import { useSettings } from "@/context/SettingsContext";
import { colorHex } from "@/lib/constants";
import { ArrowRight, Search, BookMarked, Users, Sparkles, Award, MessageSquare } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/280a934b-a6d8-4a11-9aef-a66fb0aeb566/images/d25380ef05928f9dcce99e8c01a36d0a87519f14074db0fa1a976fb8d81e695f.jpeg";

function CountUp({ value = 0, duration = 1300 }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{n.toLocaleString()}</>;
}

export default function Home() {
  usePageMeta("Antique Insulator Collector Catalog & Community", "Browse thousands of antique glass and porcelain insulators by CD number, maker and color. Grade and track your collection, trade with collectors, and join the forum.");
  const { settings } = useSettings();
  const [featured, setFeatured] = useState([]);
  const [sponsor, setSponsor] = useState(null);
  const [hasSide, setHasSide] = useState(false);
  const [stats, setStats] = useState(null);
  const [highlights, setHighlights] = useState(null);

  useEffect(() => {
    api.get("/insulators?sort=rarity&page_size=8").then(({ data }) => setFeatured(data.items)).catch(() => {});
    api.get("/ads?position=home_side").then(({ data }) => setHasSide(data.length > 0)).catch(() => {});
    api.get("/home/highlights").then(({ data }) => setHighlights(data)).catch(() => {});
    const register = sessionStorage.getItem("doh_visited")
      ? Promise.resolve()
      : api.post("/community/visit").then(() => sessionStorage.setItem("doh_visited", "1")).catch(() => {});
    register.finally(() => {
      api.get("/community/stats").then(({ data }) => setStats(data)).catch(() => {});
    });
  }, []);
  useEffect(() => {
    if (settings?.sponsor_box?.enabled) setSponsor(settings.sponsor_box);
  }, [settings]);

  const siteName = settings?.site_name || "Drops of Honey";
  const heroTitle = settings?.hero_title || "Every drop of honey, cataloged.";
  const heroSubtitle = settings?.hero_subtitle || "Browse thousands of antique glass & porcelain insulators by CD number, maker, and color.";
  const heroImage = settings?.hero_image || HERO;

  return (
    <div className="relative">
      {/* Vertical side ad rails — homepage only, large screens */}
      {hasSide && (
        <>
          <div className="hidden 2xl:block fixed left-3 top-28 w-40 z-30" data-testid="home-side-left">
            <AdSlot position="home_side" vertical label="Sponsored" />
          </div>
          <div className="hidden 2xl:block fixed right-3 top-28 w-40 z-30" data-testid="home-side-right">
            <AdSlot position="home_side" vertical label="Sponsored" />
          </div>
        </>
      )}

      {/* Banner-style header */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, #451a03ee, #78350fcc 40%, transparent)" }} />
        <img src={mediaUrl(heroImage)} alt="Insulator collection" className="absolute inset-0 w-full h-full object-cover -z-10" />
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-28 flex items-center justify-between gap-8">
          <div className="max-w-2xl float-up">
            {stats && (
              <div className="w-fit text-left mb-6" data-testid="visitor-counter">
                <p className="font-display font-bold leading-tight text-amber-200" style={{ fontSize: "0.95rem" }} data-testid="visitor-counter-card">
                  Welcome to my web site.<br />You are visitor number
                </p>
                <div className="mt-2 inline-flex gap-[2px] p-[3px] rounded" style={{ background: "linear-gradient(180deg, var(--honey-500), var(--honey-800))" }} data-testid="visitor-count-odometer">
                  {String(stats.visits ?? 0).padStart(6, "0").split("").map((d, i) => (
                    <span
                      key={i}
                      className="grid place-items-center font-tech"
                      style={{
                        width: "20px",
                        height: "28px",
                        background: "linear-gradient(180deg,#3a2a10 0%,#0d0a06 48%,#241708 52%,#0d0a06 100%)",
                        color: "var(--honey-400, #f0a83c)",
                        fontWeight: 700,
                        fontSize: "1.15rem",
                        borderRadius: "2px",
                        textShadow: "0 0 4px rgba(240,168,60,.5)",
                      }}
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <span className="inline-flex items-center gap-2 text-amber-200 font-tech text-xs uppercase tracking-widest bg-white/10 border border-white/20 rounded-full px-3 py-1">
              <Sparkles className="w-3.5 h-3.5" /> {siteName}
            </span>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-amber-50 leading-[1.05]" data-testid="hero-title">
              {heroTitle}
            </h1>
            <p className="mt-3 font-display text-2xl sm:text-3xl text-amber-200 italic" data-testid="hero-blurb">{settings?.hero_blurb || "The Numismatic Page for Insulators"}</p>
            <p className="mt-5 text-lg text-amber-100/90 max-w-xl">{heroSubtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/catalog" data-testid="hero-browse-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold" style={{ background: "var(--honey-500)", color: "#3a2a10" }}>
                <Search className="w-4 h-4" /> Browse the catalog
              </Link>
              <Link to="/register" data-testid="hero-join-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold bg-white/10 border border-white/25 text-amber-50 hover:bg-white/20 transition-colors">
                Start your collection <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="hidden lg:block shrink-0">
            <HoneyDropMorph image={settings?.logo_insulator_image} />
          </div>
        </div>
      </section>

      {/* Community stats — member counter */}
      {stats && (
        <section className="max-w-7xl mx-auto px-4 lg:px-8 -mt-10 relative z-20" data-testid="community-stats">
          <div className="flex flex-col gap-2 max-w-md">
            <div data-testid="stat-members" className="surface border-2 rounded-xl px-4 py-2.5 flex items-center gap-3 glass-glow" style={{ borderColor: "var(--honey-500)" }}>
              <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: "var(--honey-500)" }}>
                <Users className="w-4 h-4" style={{ color: "#3a2a10" }} />
              </div>
              <div className="font-display text-lg font-bold leading-none brand-text" data-testid="member-count"><CountUp value={stats.members} /></div>
              <p className="text-muted-page text-xs sm:text-sm">Collectors &amp; growing</p>
            </div>
            <div data-testid="stat-insulators" className="surface border rounded-xl px-4 py-2.5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: "var(--honey-100)" }}>
                <BookMarked className="w-4 h-4" style={{ color: "var(--honey-800)" }} />
              </div>
              <div className="font-display text-lg font-bold leading-none"><CountUp value={stats.insulators} /></div>
              <p className="text-muted-page text-xs sm:text-sm">Insulators cataloged</p>
            </div>
            <div data-testid="stat-collected" className="surface border rounded-xl px-4 py-2.5 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: "var(--honey-100)" }}>
                <Sparkles className="w-4 h-4" style={{ color: "var(--honey-800)" }} />
              </div>
              <div className="font-display text-lg font-bold leading-none"><CountUp value={stats.collected} /></div>
              <p className="text-muted-page text-xs sm:text-sm">Pieces logged</p>
            </div>
          </div>
        </section>
      )}

      {/* Members marquee — scrolling names + badges */}
      {highlights?.members?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 lg:px-8 mt-12" data-testid="members-marquee">
          <style>{`@keyframes doh-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } } .doh-track { animation: doh-marquee 40s linear infinite; } .doh-track-rev { animation: doh-marquee 55s linear infinite reverse; } .doh-marquee:hover .doh-track, .doh-marquee:hover .doh-track-rev { animation-play-state: paused; }`}</style>
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="font-tech text-xs uppercase tracking-widest brand-text">The community</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold">Meet the Collectors</h2>
            </div>
            <Link to="/leaderboard" className="text-sm font-semibold brand-text honey-underline flex items-center gap-1">Leaderboard <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="doh-marquee relative overflow-hidden surface border rounded-2xl py-4" style={{ maskImage: "linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)" }}>
            <div className="doh-track flex gap-3 w-max">
              {[...highlights.members, ...highlights.members].map((m, i) => {
                const inner = (
                  <>
                    <span className="w-9 h-9 rounded-full grid place-items-center overflow-hidden shrink-0 font-display font-bold text-white" style={{ background: "var(--honey-500)" }}>
                      {m.avatar_url ? <img src={mediaUrl(m.avatar_url)} alt="" className="w-full h-full object-cover" /> : (m.name || "?").charAt(0).toUpperCase()}
                    </span>
                    <span className="min-w-0">
                      <span className="block font-semibold text-sm leading-tight truncate max-w-[140px]">{m.name}</span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-tech uppercase tracking-wide brand-text"><Award className="w-3 h-3" /> {m.badge}</span>
                    </span>
                  </>
                );
                const cls = "shrink-0 flex items-center gap-3 pl-2 pr-4 py-2 rounded-full border surface transition-shadow";
                return m.anonymous
                  ? <span key={`anon-${i}`} className={cls} style={{ borderColor: "var(--page-border)" }}>{inner}</span>
                  : <Link key={`${m.id}-${i}`} to={`/u/${m.id}`} data-testid={i < highlights.members.length ? `marquee-member-${m.id}` : undefined} className={`${cls} hover:shadow-md`} style={{ borderColor: "var(--page-border)" }}>{inner}</Link>;
              })}
            </div>
          </div>
        </section>
      )}

      {/* Newly cataloged insulators marquee */}
      {highlights?.catalog?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 lg:px-8 mt-10" data-testid="catalog-marquee">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="font-tech text-xs uppercase tracking-widest brand-text">Fresh finds</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold">Newly Cataloged</h2>
            </div>
            <Link to="/catalog" className="text-sm font-semibold brand-text honey-underline flex items-center gap-1">Browse all <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="doh-marquee relative overflow-hidden py-1" style={{ maskImage: "linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)" }}>
            <div className="doh-track-rev flex gap-3 w-max">
              {[...highlights.catalog, ...highlights.catalog].map((c, i) => (
                <Link key={`${c.id}-${i}`} to={`/catalog/${c.id}`} data-testid={i < highlights.catalog.length ? `marquee-catalog-${c.id}` : undefined} className="shrink-0 w-56 surface border rounded-xl p-3 flex items-center gap-3 hover:shadow-md transition-shadow" style={{ borderColor: "var(--page-border)" }}>
                  {c.image_url ? (
                    <span className="w-11 h-11 rounded-lg overflow-hidden shrink-0 border" style={{ borderColor: "var(--page-border)" }}><img src={mediaUrl(c.image_url)} alt="" className="w-full h-full object-cover" /></span>
                  ) : (
                    <span className="w-11 h-11 rounded-lg grid place-items-center shrink-0 font-tech text-[10px] text-center" style={{ background: colorHex(c.color) + "33", color: "var(--honey-900)" }}>{c.cd_number || c.name?.slice(0, 6)}</span>
                  )}
                  <span className="min-w-0">
                    <span className="block font-display font-bold text-sm leading-tight truncate">{c.name}</span>
                    <span className="block text-xs text-muted-page truncate">{c.photo_added_at ? <span className="brand-text font-semibold">★ New photo</span> : ([c.maker, c.color].filter(Boolean).join(" · ") || "—")}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Top ad row */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 mt-10">
        <AdSlot position="home_top" />
      </div>

      {/* Feature strip */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14 grid gap-6 md:grid-cols-3 stagger">
        {[
          { icon: Search, title: "Reference Catalog", desc: "Search by CD number, maker, color and rarity — Numista-style filtering built for insulators." },
          { icon: BookMarked, title: "Grade & Track", desc: "Log what you own with a 7-tier grade, quantity, purchase price and storage location." },
          { icon: Users, title: "Collector Community", desc: "Discuss finds in the forum and message collectors to swap and trade." },
        ].map((f) => (
          <div key={f.title} className="surface border rounded-2xl p-6 glass-glow card-lift">
            <div className="w-11 h-11 rounded-xl grid place-items-center mb-4" style={{ background: "var(--honey-100)" }}>
              <f.icon className="w-5 h-5" style={{ color: "var(--honey-800)" }} />
            </div>
            <h3 className="font-display text-2xl font-bold">{f.title}</h3>
            <p className="text-muted-page mt-1">{f.desc}</p>
          </div>
        ))}
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 pb-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="font-tech text-xs uppercase tracking-widest brand-text">Grail pieces</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold">Featured Rarities</h2>
          </div>
          <Link to="/catalog" className="text-sm font-semibold brand-text honey-underline flex items-center gap-1">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4 stagger">
          {featured.map((ins) => <InsulatorCard key={ins.id} ins={ins} />)}
        </div>
      </section>

      {/* Forums marquee — scrolling topics */}
      {highlights?.forums?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 lg:px-8 py-8" data-testid="home-forums">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="font-tech text-xs uppercase tracking-widest brand-text">Talk shop</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold">From the Forums</h2>
            </div>
            <Link to="/forum" className="text-sm font-semibold brand-text honey-underline flex items-center gap-1">All discussions <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="doh-marquee relative overflow-hidden py-1" style={{ maskImage: "linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)" }}>
            <div className="doh-track flex gap-3 w-max">
              {[...highlights.forums, ...highlights.forums].map((t, i) => (
                <Link key={`${t.id}-${i}`} to={`/forum/${t.id}`} data-testid={i < highlights.forums.length ? `home-forum-${t.id}` : undefined} className="shrink-0 w-72 surface border rounded-2xl p-5 hover:shadow-lg transition-shadow block" style={{ borderColor: "var(--page-border)" }}>
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-tech uppercase tracking-widest px-2 py-1 rounded-full mb-3" style={{ background: "var(--honey-100)", color: "var(--honey-900)" }}><MessageSquare className="w-3 h-3" /> {t.category || "General"}</span>
                  <h3 className="font-display text-lg font-bold leading-snug line-clamp-2">{t.title}</h3>
                  <p className="text-sm text-muted-page mt-2 truncate">{t.author_name ? `by ${t.author_name} · ` : ""}{t.replies} {t.replies === 1 ? "reply" : "replies"}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sponsor box + advertise CTA */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14 grid gap-6 lg:grid-cols-2">
        {sponsor && (
          <a href={sponsor.target_url || "#"} target="_blank" rel="noopener noreferrer" data-testid="sponsor-box" className="surface border rounded-2xl overflow-hidden flex items-center gap-4 p-2 hover:shadow-lg transition-shadow">
            {sponsor.image_url ? (
              <img src={sponsor.image_url} alt="" className="w-32 h-32 object-cover rounded-xl" />
            ) : (
              <div className="w-32 h-32 rounded-xl grid place-items-center" style={{ background: "var(--honey-100)" }}><Sparkles className="w-8 h-8 brand-text" /></div>
            )}
            <div className="pr-4">
              <p className="font-tech text-[10px] uppercase tracking-widest text-muted-page">Featured Sponsor</p>
              <h3 className="font-display text-2xl font-bold">{sponsor.title}</h3>
              <p className="text-muted-page text-sm">{sponsor.text}</p>
            </div>
          </a>
        )}
        <div className="rounded-2xl p-8 text-amber-50 flex flex-col justify-center" style={{ background: "linear-gradient(130deg, #78350f, #b45309)" }}>
          <h3 className="font-display text-3xl font-bold">Advertise with {siteName}</h3>
          <p className="text-amber-100/85 mt-2">Reach dedicated insulator collectors. Buy a homepage ad block and get seen by the community.</p>
          <Link to="/advertise" data-testid="home-advertise-btn" className="mt-5 w-fit inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold" style={{ background: "var(--honey-500)", color: "#3a2a10" }}>
            See ad packages <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
